import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, SectionLabel } from "@/components/landing/shared";
import { PLATFORM_OPTS, VIBE_OPTS, LENGTH_OPTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  fullName: "",
  brandName: "",
  email: "",
  heroProduct: "",
  brandAssetsLink: "",
  targetPlatforms: "",
  brandVibe: "",
  keyMessage: "",
  videoLength: "",
};

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="ff-mono text-xs uppercase tracking-widest text-zinc-500">
      {label}
    </Label>
    {children}
  </div>
);

const inputCls =
  "border-zinc-800 bg-[#0a0a0a] text-white placeholder:text-zinc-600 focus-visible:ring-white/40 focus-visible:border-zinc-600";

export const BriefForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["fullName", "brandName", "email", "heroProduct", "targetPlatforms", "brandVibe", "keyMessage", "videoLength"];
    for (const r of required) {
      if (!form[r].trim()) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/brief`, form);
      setDone(true);
      toast.success("Brief received! Your free sample lands within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="brief-form" className="border-t border-zinc-900 py-24 sm:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <SectionLabel testid="form-label">Start here</SectionLabel>
          <h2 className="mt-6 font-display text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
            Claim your free 24-hour sample.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400">
            Five quick details and we'll build your custom motion-ad preview within 24 hours. No
            card, no commitment.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-12 rounded-3xl border border-zinc-800 bg-[#0d0d0d] p-12 text-center"
              data-testid="form-success"
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#C7FF3C] text-black">
                <Check className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold">Brief received.</h3>
              <p className="mt-3 text-zinc-400">
                Your custom sample is on the way — check your inbox within 24 hours.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="form-submit-another"
                className="mt-8 rounded-full border border-zinc-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white"
              >
                Submit another brief
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-12 space-y-6" data-testid="brief-form-element">
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Your name" htmlFor="fullName">
                  <Input id="fullName" data-testid="input-fullName" className={inputCls} placeholder="Jane Doe" value={form.fullName} onChange={set("fullName")} />
                </Field>
                <Field label="Brand / company" htmlFor="brandName">
                  <Input id="brandName" data-testid="input-brandName" className={inputCls} placeholder="AeroTech Apparel" value={form.brandName} onChange={set("brandName")} />
                </Field>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Work email" htmlFor="email">
                  <Input id="email" type="email" data-testid="input-email" className={inputCls} placeholder="jane@aerotech.com" value={form.email} onChange={set("email")} />
                </Field>
                <Field label="Hero product & URL" htmlFor="heroProduct">
                  <Input id="heroProduct" data-testid="input-heroProduct" className={inputCls} placeholder="yourbrand.com/product" value={form.heroProduct} onChange={set("heroProduct")} />
                </Field>
              </div>

              <Field label="Assets link (optional)" htmlFor="brandAssetsLink">
                <Input id="brandAssetsLink" data-testid="input-brandAssetsLink" className={inputCls} placeholder="Google Drive, Dropbox or Figma link" value={form.brandAssetsLink} onChange={set("brandAssetsLink")} />
              </Field>

              <div className="grid gap-6 sm:grid-cols-3">
                <Field label="Platform">
                  <Select value={form.targetPlatforms} onValueChange={setSel("targetPlatforms")}>
                    <SelectTrigger data-testid="select-platform" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-[#0d0d0d] text-white">
                      {PLATFORM_OPTS.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Vibe">
                  <Select value={form.brandVibe} onValueChange={setSel("brandVibe")}>
                    <SelectTrigger data-testid="select-vibe" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-[#0d0d0d] text-white">
                      {VIBE_OPTS.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Length">
                  <Select value={form.videoLength} onValueChange={setSel("videoLength")}>
                    <SelectTrigger data-testid="select-length" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-[#0d0d0d] text-white">
                      {LENGTH_OPTS.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Key offer / headline message" htmlFor="keyMessage">
                <Textarea id="keyMessage" data-testid="input-keyMessage" className={inputCls} rows={4} placeholder="E.g. 20% off our bestseller — 50% lighter than standard jackets" value={form.keyMessage} onChange={set("keyMessage")} />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C7FF3C] px-8 py-4 text-sm font-semibold text-black transition-colors hover:bg-[#b4ec2a] disabled:opacity-60 sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending brief…
                  </>
                ) : (
                  <>
                    Submit brief & get free 24h sample
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
